import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Zap, Flame, ArrowRight, Check, Sparkles, Gift } from 'lucide-react';
import { KITS } from '../data/kits';
import { UPSELLS } from '../data/upsells';
import { PaymentMethods } from './PaymentMethods';
interface BundleUpsellPopupProps {
  open: boolean;
  selectedKitIds: string[];
  onAccept: () => void;
  onDecline: () => void;
}
const DISCOUNT_PCT = 30;
export function BundleUpsellPopup({
  open,
  selectedKitIds,
  onAccept,
  onDecline
}: BundleUpsellPopupProps) {
  const selectedKits = KITS.filter(k => selectedKitIds.includes(k.id));
  const mainTotal = selectedKits.reduce((sum, k) => sum + k.price, 0);
  const bumpsTotal = UPSELLS.reduce((sum, u) => sum + u.price, 0);
  const discountedBumps = Math.round(bumpsTotal * (1 - DISCOUNT_PCT / 100));
  const trueTotal = mainTotal + bumpsTotal;
  const finalBundle = mainTotal + discountedBumps;
  const savings = trueTotal - finalBundle;
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDecline();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onDecline]);
  return <AnimatePresence data-id="element-30">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6 bg-black/75 backdrop-blur-sm overflow-y-auto" onClick={onDecline} role="dialog" aria-modal="true" aria-labelledby="bundle-upsell-title" data-id="element-31">
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-cream rounded-[1.75rem] shadow-2xl w-full max-w-lg overflow-hidden my-4 max-h-[95vh] flex flex-col" data-id="element-32">
            <button onClick={onDecline} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-ink-light hover:text-ink flex items-center justify-center shadow-md transition-colors" data-id="element-33">
              <X className="w-4 h-4" data-id="element-34" />
            </button>
            {/* Header banner */}
            <div className="relative bg-gradient-to-br from-combo-green via-combo-green to-[#0f2d20] text-white px-4 sm:px-5 pt-3 sm:pt-5 pb-4 sm:pb-5 text-center overflow-hidden" data-id="element-35">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(252,211,77,0.22),transparent_55%)]" data-id="element-36" />
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-2xl" data-id="element-37" />
              <div className="absolute -bottom-12 -right-10 w-44 h-44 bg-black/20 rounded-full blur-2xl" data-id="element-38" />
              <motion.div initial={{
            scale: 0,
            rotate: -10
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 260,
            damping: 18,
            delay: 0.05
          }} className="relative inline-flex mb-1.5" data-id="element-39">
                <motion.div animate={{
              scale: [1, 1.08, 1]
            }} transition={{
              duration: 1.6,
              repeat: Infinity
            }} className="absolute inset-0 bg-yellow-300/40 rounded-full blur-md" data-id="element-40" />
                <div className="relative w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-yellow-400 text-combo-green flex items-center justify-center shadow-lg border-2 sm:border-[3px] border-white/20" data-id="element-41">
                  <Gift className="w-4 h-4 sm:w-6 sm:h-6" strokeWidth={2.5} data-id="element-42" />
                </div>
              </motion.div>
              <motion.div animate={{
            scale: [1, 1.03, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="relative inline-flex items-center gap-1 sm:gap-1.5 bg-white/15 backdrop-blur-sm text-yellow-200 px-2 sm:px-2.5 py-0.5 rounded-full font-extrabold text-[8px] sm:text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.16em] mb-1.5 sm:mb-2 border border-yellow-300/40" data-id="element-43">
                <Flame className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-44" />
                Última oportunidad real
                <Flame className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-45" />
              </motion.div>
              <h2 id="bundle-upsell-title" className="relative font-sans font-extrabold text-[15px] sm:text-2xl leading-tight tracking-tight mb-1 sm:mb-1.5" data-id="element-46">
                Está bien... pero mirá esto 👀
              </h2>
              <p className="relative text-[11px] sm:text-sm text-white/95 max-w-sm mx-auto leading-snug" data-id="element-47">
                Llevate <strong className="text-yellow-300" data-id="element-48">los 6 kits</strong>{' '}
                complementarios al{' '}
                <span className="font-bold text-yellow-300 underline decoration-yellow-300/60 underline-offset-2" data-id="element-49">
                  30% OFF
                </span>{' '}
                — la oferta más conveniente de toda la página.
              </p>
            </div>
            {/* Body */}
            <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4 flex-1 overflow-y-auto" data-id="element-50">
              <div className="flex justify-center mb-2.5" data-id="element-51">
                <motion.div animate={{
              opacity: [1, 0.55, 1],
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 1,
              repeat: Infinity
            }} className="bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-1 rounded-full font-extrabold text-[10px] sm:text-[11px] shadow-lg border-2 border-white tracking-wide" data-id="element-52">
                  🎁 TODOS LOS COMPLEMENTOS · 30% OFF
                </motion.div>
              </div>
              <p className="text-[12px] sm:text-sm text-ink leading-snug mb-3 text-center" data-id="element-53">
                Resolvé <strong data-id="element-54">cada situación</strong> de tu alimentación de
                una sola vez — familia, salud, deporte y restricciones, todo
                cubierto. Esta pantalla no aparece dos veces.
              </p>
              {/* Included */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-ink/5 mb-2.5" data-id="element-55">
                <div className="font-bold text-[10px] text-ink uppercase tracking-wider mb-1.5" data-id="element-56">
                  ✅ Esto te llevás hoy:
                </div>
                <ul className="space-y-1" data-id="element-57">
                  {selectedKits.map(kit => <li key={kit.id} className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-58">
                      <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-59" />
                      <span data-id="element-60">
                        <strong data-id="element-61">{kit.name}</strong>{' '}
                        <span className="text-ink-light text-[10px]" data-id="element-62">
                          (kit principal)
                        </span>
                      </span>
                    </li>)}
                  <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-63">
                    <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-64" />
                    <span data-id="element-65">
                      <strong data-id="element-66">Acceso de por vida</strong> + actualizaciones
                    </span>
                  </li>
                </ul>
                <div className="border-t border-ink/10 mt-2 pt-2" data-id="element-67">
                  <div className="font-bold text-[10px] text-ink uppercase tracking-wider mb-1.5 flex items-center gap-1.5" data-id="element-68">
                    <Sparkles className="w-3 h-3" style={{
                  color: '#C9A84C'
                }} data-id="element-69" />
                    + 6 kits complementarios al 30% OFF
                  </div>
                  <div className="flex flex-wrap gap-1" data-id="element-70">
                    {UPSELLS.map(u => <span key={u.id} className="inline-flex items-center gap-1 bg-cream-dark text-ink text-[10px] font-medium px-1.5 py-0.5 rounded" data-id="element-71">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                    backgroundColor: u.color
                  }} data-id="element-72" />
                        {u.name.replace('Kit ', '')}
                      </span>)}
                  </div>
                </div>
              </div>
              {/* Pricing */}
              <div className="bg-gradient-to-br from-cream-dark to-cream-dark/80 rounded-xl p-3 border border-gold/30" data-id="element-73">
                <div className="flex items-center justify-between gap-3" data-id="element-74">
                  <div className="text-left min-w-0" data-id="element-75">
                    <div className="text-[9px] text-ink-light uppercase tracking-wider font-bold" data-id="element-76">
                      Tu paquete completo
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5" data-id="element-77">
                      <motion.span animate={{
                    scale: [1, 1.04, 1]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity
                  }} className="text-xl sm:text-2xl font-extrabold text-combo-green tabular-nums" data-id="element-78">
                        ${finalBundle.toLocaleString('es-AR')}
                      </motion.span>
                      <span className="text-[11px] sm:text-xs text-ink-light line-through decoration-red-500 decoration-2 tabular-nums" data-id="element-79">
                        ${trueTotal.toLocaleString('es-AR')}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0" data-id="element-80">
                    <div className="inline-flex items-center gap-1 bg-combo-green text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-81">
                      Ahorrás ${savings.toLocaleString('es-AR')}
                    </div>
                    <div className="text-[9px] text-ink-light mt-1 flex items-center justify-end gap-1" data-id="element-82">
                      <Lock className="w-2.5 h-2.5 text-combo-green" data-id="element-83" />
                      Solo en esta pantalla
                    </div>
                  </div>
                </div>
              </div>
              <PaymentMethods className="mt-3" data-id="element-84" />
            </div>
            {/* CTAs */}
            <div className="px-4 sm:px-6 py-3 sm:py-3.5 bg-white border-t border-ink/5 space-y-1.5" data-id="element-85">
              <motion.button onClick={onAccept} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} animate={{
            boxShadow: ['0 0 0 0 rgba(27,67,50,0.55)', '0 0 0 14px rgba(27,67,50,0)', '0 0 0 0 rgba(27,67,50,0)']
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="w-full bg-combo-green text-white py-2.5 sm:py-3 rounded-full font-extrabold text-[12px] sm:text-sm hover:bg-combo-green/90 transition-colors flex items-center justify-center gap-1.5 shadow-glow" data-id="element-86">
                <span className="truncate" data-id="element-87">
                  ¡SÍ! LLEVAR TODO POR ${finalBundle.toLocaleString('es-AR')}
                </span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" data-id="element-88" />
              </motion.button>
              <button onClick={onDecline} className="w-full text-ink-light hover:text-ink text-[11px] sm:text-xs font-medium underline underline-offset-4 transition-colors py-1" data-id="element-89">
                No gracias, finalizar mi compra así →
              </button>
              <div className="flex items-center justify-center gap-3 text-[9px] sm:text-[10px] text-ink-light font-medium" data-id="element-90">
                <span className="flex items-center gap-1" data-id="element-91">
                  <Lock className="w-2.5 h-2.5 text-combo-green" data-id="element-92" /> Pago Seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-93">
                  <Zap className="w-2.5 h-2.5 text-gold" data-id="element-94" /> Acceso Inmediato
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}